/**
 * Upcoming invoice — the headline of the subscription tab: what this org is
 * about to be charged and when, with the Stripe line items behind it.
 * Discounts and applied credit render as explicit rows: Stripe reports
 * line.amount before discounts, so without them the lines don't add up to
 * the amount due. Renders nothing when there is no upcoming invoice.
 */
import { Fragment } from 'react';
import { alpha, Box, Card, CardContent, Chip, Typography } from '@mui/material';

import { CardTitle } from './card-title';

interface UpcomingInvoiceDiscount {
  customer_label?: string | null;
  reason?: string | null;
  amount?: number | null;
}

interface UpcomingInvoiceLine {
  description?: string | null;
  amount?: number | null;
  proration?: boolean | null;
  period_start?: number | null;
  period_end?: number | null;
  discounts?: UpcomingInvoiceDiscount[] | null;
}

export interface UpcomingInvoiceLike {
  amount_due?: number | null;
  next_payment_attempt?: number | null;
  period_end?: number | null;
  lines?: UpcomingInvoiceLine[] | null;
  subtotal?: number | null;
  total?: number | null;
  credit_applied?: number | null;
  total_discount_amount?: number | null;
  subscription_discounts?: UpcomingInvoiceDiscount[] | null;
}

const formatCents = (cents?: number | null): string =>
  typeof cents === 'number'
    ? (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
      })
    : '—';

const formatEpochDate = (epochSeconds?: number | null): string | null =>
  typeof epochSeconds === 'number'
    ? new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

const TotalRow = ({
  label,
  amount,
  credit,
  emphasize,
  divider,
}: {
  label: string;
  amount: string;
  credit?: boolean;
  emphasize?: boolean;
  divider?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 1,
      py: 0.5,
      borderTop: divider ? 1 : 0,
      borderColor: 'divider',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: emphasize ? 600 : 400,
        color: credit ? 'success.main' : 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        width: 88,
        textAlign: 'right',
        fontWeight: emphasize ? 600 : 500,
        color: credit ? 'success.main' : 'text.primary',
      }}
    >
      {amount}
    </Typography>
  </Box>
);

export const OrganizationUpcomingInvoiceCard = ({
  upcoming,
}: {
  upcoming?: UpcomingInvoiceLike | null;
}) => {
  if (!upcoming) return null;

  const paymentDate = formatEpochDate(upcoming.next_payment_attempt);
  const periodEnd = formatEpochDate(upcoming.period_end);
  const hasLineDiscounts = (upcoming.lines ?? []).some(
    line => (line.discounts ?? []).length > 0,
  );
  const creditApplied = upcoming.credit_applied ?? 0;

  return (
    <Card
      sx={{
        mt: 2,
        border: 1,
        borderColor: 'primary.main',
        bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <CardTitle sx={{ color: 'primary.main' }}>
              Upcoming invoice
            </CardTitle>
            <Typography variant="h5" sx={{ fontWeight: 600, mt: 0.5 }}>
              {formatCents(upcoming.amount_due)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {paymentDate
                ? `Payment on ${paymentDate}`
                : periodEnd
                  ? `Period ends ${periodEnd}`
                  : 'Schedule pending'}
            </Typography>
          </Box>
          {!!upcoming.lines?.length && (
            <Box sx={{ flex: 2, minWidth: 280 }}>
              {upcoming.lines.map((line, index) => (
                <Fragment key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      py: 0.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {line.description || '(no description)'}
                      {line.proration && (
                        <Chip
                          label="proration"
                          size="xSmall"
                          variant="soft"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    {line.period_start && line.period_end && (
                      <Typography variant="caption" color="text.disabled">
                        {new Date(line.period_start * 1000).toLocaleDateString()}
                        {' – '}
                        {new Date(line.period_end * 1000).toLocaleDateString()}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        width: 88,
                        textAlign: 'right',
                        fontWeight: 500,
                        color:
                          (line.amount ?? 0) < 0 ? 'success.main' : 'text.primary',
                      }}
                    >
                      {formatCents(line.amount)}
                    </Typography>
                  </Box>
                  {/* Stripe reports line.amount before discounts, so without
                      these rows the lines don't add up to the total. */}
                  {(line.discounts ?? []).map((discount, discountIndex) => (
                    <Box
                      key={discountIndex}
                      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, pb: 0.5, pl: 2 }}
                    >
                      {/* Customer label leads because this block previews their
                          invoice; the reason follows as internal context. */}
                      <Typography variant="caption" sx={{ flex: 1, color: 'success.main' }}>
                        {discount.customer_label || 'Discount'}
                        {discount.reason && (
                          <Typography component="span" variant="caption" color="text.disabled">
                            {' '}
                            ({discount.reason})
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ width: 88, textAlign: 'right', fontWeight: 500, color: 'success.main' }}
                      >
                        −{formatCents(discount.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Fragment>
              ))}

              {/* Subtotal only earns its place once a line discount has moved
                  it off the line sum. */}
              {hasLineDiscounts && (
                <TotalRow label="Subtotal" amount={formatCents(upcoming.subtotal)} divider />
              )}

              {/* Not nested under a line: these apply to the whole subscription. */}
              {(upcoming.subscription_discounts ?? []).map((discount, index) => (
                <TotalRow
                  key={index}
                  label={`${discount.customer_label || 'Discount'} (whole subscription${
                    discount.reason ? ` · ${discount.reason}` : ''
                  })`}
                  amount={`−${formatCents(discount.amount)}`}
                  credit
                />
              ))}

              {creditApplied > 0 ? (
                <>
                  <TotalRow label="Total" amount={formatCents(upcoming.total)} divider />
                  {/* Credit is drawn after discounts, which is why total and
                      amount due differ. */}
                  <TotalRow
                    label="Credit applied"
                    amount={`−${formatCents(creditApplied)}`}
                    credit
                  />
                  <TotalRow
                    label="Amount due"
                    amount={formatCents(upcoming.amount_due)}
                    emphasize
                    divider
                  />
                </>
              ) : (
                (upcoming.total_discount_amount ?? 0) > 0 && (
                  <TotalRow
                    label="Total"
                    amount={formatCents(upcoming.amount_due)}
                    emphasize
                    divider
                  />
                )
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
